<script>
import SelectWithSearch from "@/views/pages/Dilfoods/Common/SelectWithSearch.vue";
import { GravyPath, BrandsPath, OpsPath } from "@/path";
import Swal from "sweetalert2";
import axios from "axios";

export default {
  components: { SelectWithSearch },
  props: {
    gravies: { type: Array, default: () => [] },
    option: { type: Object, required: true }, // { id, title }
  },
  data() {
    return {
      fields: ["gravy", "gm_usage", "brand", "actions"],
      current_page: 1,
      base_url: process.env.VUE_APP_API_BACKEND_URL,

      // form
      form: {
        op_id: this.option?.id || null,
        gravy_id: null,
        brand_ids: [],
        usage: null, // API create expects 'usage' (fallback to gm_usage if needed)
      },
      selectedGravy: null,

      // UX
      key: 1,
      saving: false,
      deletingIds: new Set(),

      // Local rendering store (instant updates without refresh)
      localRows: [],
      tableKey: 0,
    };
  },
  computed: {
    GravyPath() {
      return GravyPath;
    },
    BrandsPath() {
      return BrandsPath;
    },
    canSave() {
      return (
        !!this.form.op_id &&
        !!this.form.gravy_id &&
        this.form.usage !== null &&
        this.form.usage !== "" &&
        Number(this.form.usage) >= 0 &&
        Array.isArray(this.form.brand_ids) &&
        this.form.brand_ids.length > 0 &&
        !this.saving
      );
    },
  },
  watch: {
    // Reset form if modal is opened for a different option
    option: {
      handler(n) {
        this.resetForm(n?.id || null);
      },
      deep: false,
    },

    // Keep localRows in sync with parent prop, and re-render table
    gravies: {
      handler(newVal) {
        this.localRows = Array.isArray(newVal)
          ? JSON.parse(JSON.stringify(newVal))
          : [];
        this.tableKey += 1;
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    resetForm(opId) {
      this.form = {
        op_id: opId,
        gravy_id: null,
        brand_ids: [],
        usage: null,
      };
      this.selectedGravy = null;
      this.key += 1;
    },

    setGravy(v) {
      this.form.gravy_id = v?.id || null;
      this.selectedGravy = v || null;
      this.key += 1;
    },

    onBrandsSelected(v) {
      const arr = Array.isArray(v) ? v : [];
      this.form.brand_ids = arr.map((x) => Number(x?.id ?? x)).filter(Boolean);
    },

    async saveGravy() {
      if (
        !this.option?.id ||
        !this.form.gravy_id ||
        this.form.usage === null ||
        this.form.usage === "" ||
        !this.form.brand_ids?.length
      ) {
        Swal.fire(
          "Missing fields",
          "Select gravy, enter usage, and choose brands.",
          "warning"
        );
        return;
      }

      this.saving = true;
      const baseUrl = this.base_url + OpsPath.SAVEOPSGRAVY;

      // primary payload (usage)
      const payload = {
        op_id: Number(this.option.id),
        gravy_id: Number(this.form.gravy_id),
        usage: Number(this.form.usage),
        brand_ids: this.form.brand_ids.map(Number),
      };

      // single fallback if server expects gm_usage key name
      const fallback = {
        ...payload,
        gm_usage: payload.usage,
      };
      delete fallback.usage;

      try {
        let res = await axios.post(baseUrl, payload, {
          headers: { "Content-Type": "application/json" },
        });
        if (!res.data?.status) {
          res = await axios.post(baseUrl, fallback, {
            headers: { "Content-Type": "application/json" },
          });
        }

        if (res.data?.status) {
          Swal.fire("Updated Successfully", "", "success");

          // ✔ Use API response as-is (array or single)
          const apiRows = Array.isArray(res.data.data)
            ? res.data.data
            : [res.data.data];

          // Shape it to your table’s row format
          const newRows = apiRows.map((m) => ({
            op_id: m.op_id,
            brand_id: m.brand_id,
            gravy_id: m.gravy_id,
            gm_usage: m.gm_usage, // from API
            gravy_name: m.gravy?.name, // from API
            units: m.gravy?.units, // from API
            brand_name: m.brand?.name, // from API
          }));

          // ✅ Instant in-modal update (merge by composite key)
          const byKey = new Map(
            this.localRows.map((g) => [
              `${g.op_id}-${g.brand_id}-${g.gravy_id}`,
              g,
            ])
          );
          newRows.forEach((n) => {
            byKey.set(`${n.op_id}-${n.brand_id}-${n.gravy_id}`, n);
          });
          this.localRows = Array.from(byKey.values());
          this.tableKey += 1;

          // Notify parent so main list stays in sync
          this.$emit("gravies-updated", {
            optionId: this.option.id,
            newGravies: newRows,
          });

          // Ready for next quick entry
          this.resetForm(this.option.id);
        } else {
          Swal.fire(
            "Error",
            res.data?.message || "Failed to save gravy",
            "error"
          );
        }
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to save gravy", "error");
      } finally {
        this.saving = false;
      }
    },

    async deleteRow(row) {
      const g = row.item;
      const payload = {
        gravy_id: g.gravy_id,
        brand_id: g.brand_id,
        op_id: g.op_id,
        usage: g.gm_usage, // your delete endpoint expects "usage"
      };
      const key = `${g.op_id}-${g.brand_id}-${g.gravy_id}`;

      try {
        this.deletingIds.add(key);
        const res = await axios.post(
          this.base_url + OpsPath.DELETEOPSGRAVY,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        if (res.data.status) {
          Swal.fire("Deleted Successfully", "", "success");

          // Remove from modal table immediately
          this.localRows = this.localRows.filter(
            (r) => `${r.op_id}-${r.brand_id}-${r.gravy_id}` !== key
          );
          this.tableKey += 1;

          // Notify parent to update list row
          this.$emit("gravy-deleted", {
            optionId: this.option.id,
            gravyKey: key,
          });
        } else {
          Swal.fire(
            "Error",
            res.data.message || "Failed to delete gravy",
            "error"
          );
        }
      } catch (error) {
        console.log(error);
        Swal.fire("Error", "Failed to delete gravy", "error");
      } finally {
        this.deletingIds.delete(key);
      }
    },
  },
};
</script>

<template>
  <div>
    <!-- Form -->
    <div class="mb-3">
      <b-row>
        <b-col sm="4">
          <label>Gravy</label>
          <SelectWithSearch
            :search_api="GravyPath.SEARCH_GRAVIES"
            track_by="id"
            display="name"
            :full_object="true"
            @selectedOption="setGravy"
          />
        </b-col>

        <b-col sm="4">
          <label :key="key">Usage ({{ selectedGravy?.units || "gm" }})</label>
          <b-input v-model.number="form.usage" type="number" min="0"></b-input>
          <small
            v-if="form.usage === null || form.usage === ''"
            class="text-muted"
          >
            Enter usage in {{ selectedGravy?.units || "gm" }}
          </small>
        </b-col>

        <b-col sm="4">
          <label>Brands</label>
          <SelectWithSearch
            :search_api="BrandsPath.BRAND_SEARCH"
            track_by="id"
            display="label"
            :multi_select="true"
            @selectedOption="onBrandsSelected"
          />
          <small v-if="!form.brand_ids.length" class="text-muted">
            Select one or more brands
          </small>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <b-button :disabled="!canSave" variant="success" @click="saveGravy">
            <b-spinner small v-if="saving" class="mr-2" /> Save
          </b-button>
          <b-button
            class="ml-2"
            variant="outline-secondary"
            @click="resetForm(option?.id)"
          >
            Clear
          </b-button>
        </b-col>
      </b-row>
    </div>

    <!-- Table -->
    <b-table
      :key="tableKey"
      :items="localRows"
      :fields="fields"
      :per-page="10"
      responsive="sm"
      :current-page="current_page"
      show-empty
      empty-text="No gravies tagged yet"
    >
      <template #cell(gravy)="row">
        {{ row.item.gravy_name }}
      </template>

      <template #cell(brand)="row">
        {{ row.item.brand_name }}
      </template>

      <template #cell(gm_usage)="row">
        {{ row.item.gm_usage }} {{ row.item.units }}
      </template>

      <template #cell(actions)="row">
        <b-button
          variant="danger"
          @click="deleteRow(row)"
          :disabled="
            deletingIds.has(
              `${row.item.op_id}-${row.item.brand_id}-${row.item.gravy_id}`
            )
          "
        >
          <i class="fa fa-trash"></i>
        </b-button>
      </template>
    </b-table>

    <!-- Pagination -->
    <div class="row">
      <div class="col">
        <div class="dataTables_paginate paging_simple_numbers float-right">
          <ul class="pagination pagination-rounded mb-0">
            <b-pagination
              v-model="current_page"
              :total-rows="localRows.length"
              :per-page="10"
            ></b-pagination>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
